"use client";

import { useMemo } from "react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { weekRange } from "@/lib/utils/dates";
import { useWeekCompletions } from "./useWeekCompletions";
import { useChildMembers, useMembers } from "./useMembers";
import { useGoals } from "./useGoals";
import { useHouseholdGoals } from "./useHouseholdGoals";
import { useCompletions } from "./useCompletions";
import { isFamilyGoal, isIndividualMoneyGoal } from "@/lib/schemas";
import type {
  Completion,
  FamilyCountGoal,
  FamilyOfflineCountGoal,
  IndividualMoneyGoal,
} from "@/lib/schemas";

export type RecapTopMoment = {
  childName: string;
  choreTitle: string;
  dayName: string;
  rewardMinor: number;
  kind: Completion["kind"];
};

export type RecapPerChild = {
  uid: string;
  name: string;
  count: number;
};

export type RecapPersonalGoal = {
  goalId: string;
  title: string;
  emoji?: string;
  balanceMinor: number;
  targetMinor: number;
  pct: number;
};

export type RecapFamilyGoalProgress = {
  goal: FamilyCountGoal | FamilyOfflineCountGoal;
  progress: number;
  pct: number;
};

export type WeeklyRecap = {
  loading: boolean;
  weekStart: Date;
  weekEnd: Date;
  title: string;

  /** Tylko zarobki viewer'a (jego completion z kind=extra). Dla parenta = 0. */
  personalEarnedMinor: number;
  prevPersonalEarnedMinor: number;
  diffPersonalEarnedMinor: number;

  /** Pomoc rodzinie — całe gospodarstwo, breakdown per dziecko. */
  familyHelpCount: number;
  familyHelpPerChild: RecapPerChild[];

  /** Aktywności offline — całe gospodarstwo, breakdown per dziecko. */
  offlineCount: number;
  offlinePerChild: RecapPerChild[];

  /** Wszystkie approved — całe gospodarstwo, breakdown per dziecko. */
  approvedCount: number;
  approvedPerChild: RecapPerChild[];
  totalCount: number;

  topMoment: RecapTopMoment | null;
  /** Cele oszczędzania viewer'a (filtrujemy po jego uid). */
  personalGoals: RecapPersonalGoal[];
  /** Aktywny cel rodzinny + bieżący progres (od goal.createdAt). */
  familyGoal: RecapFamilyGoalProgress | null;

  hasAnyData: boolean;
};

/**
 * Tytuł recap'a zmienia się z dniem:
 * - piątek = początek weekendu, zaczepka pozytywna
 * - sobota/niedziela = tydzień się domyka
 */
export function recapTitleFor(now: Date): string {
  const day = now.getDay(); // 0=ND, 5=PT, 6=SO
  if (day === 5) return "Piątek — weekendu początek";
  return "Ten tydzień się kończy";
}

/**
 * Recap tygodniowy widoczny po piątku. Statystyki rozdzielone:
 *  - household-wide z breakdown per dziecko: pomoc rodzinie, offline, zrobione
 *  - tylko viewer: zarobione z extras + jego cele oszczędzania
 *  - aktywny cel rodzinny + globalny progres
 *
 * `viewerUid` (zwykle currentUser uid) decyduje co znaczy "personal".
 * Bez viewerUid → personal* = 0, personalGoals = [].
 */
export function useWeeklyRecap(
  householdId: string | undefined,
  viewerUid?: string,
): WeeklyRecap {
  const thisWeek = useMemo(() => weekRange(), []);
  const prevWeek = useMemo(() => {
    const d = new Date(thisWeek.start);
    d.setDate(d.getDate() - 7);
    return weekRange(d);
  }, [thisWeek.start]);

  const cur = useWeekCompletions(householdId, thisWeek.start, thisWeek.end);
  const prev = useWeekCompletions(householdId, prevWeek.start, prevWeek.end);
  const { members: children, loading: membersLoading } = useChildMembers(householdId);
  // members all (parent też) — żeby personal earnings dla parenta i breakdown
  // mogły działać niezależnie od role.
  const { members: allMembers } = useMembers(householdId);
  const { goals, loading: goalsLoading } = useGoals(householdId);
  const { goals: householdGoals } = useHouseholdGoals(householdId);
  // Approved completions w HH (od dawna) — potrzebne do progresu celu rodzinnego
  // (od goal.createdAt). useGoalProgress tego nie wystawia jako re-export, ale
  // używamy tego samego listenera tutaj.
  const { completions: approvedAll } = useCompletions(householdId, {
    status: "approved",
  });

  return useMemo(() => {
    const approved = cur.completions.filter((c) => c.status === "approved");
    const prevApproved = prev.completions.filter((c) => c.status === "approved");

    // Personal earnings — tylko viewer's extras.
    const personalEarnedMinor = viewerUid
      ? approved
          .filter((c) => c.kind === "extra" && c.completedBy === viewerUid)
          .reduce((sum, c) => sum + c.rewardMinor, 0)
      : 0;
    const prevPersonalEarnedMinor = viewerUid
      ? prevApproved
          .filter((c) => c.kind === "extra" && c.completedBy === viewerUid)
          .reduce((sum, c) => sum + c.rewardMinor, 0)
      : 0;

    const familyApproved = approved.filter((c) => c.kind === "family");
    const offlineApproved = approved.filter((c) => c.offline);

    const memberByUid = new Map(allMembers.map((m) => [m.userId, m]));

    const buildPerChild = (
      completions: readonly Completion[],
    ): RecapPerChild[] => {
      const counts = new Map<string, number>();
      for (const c of completions) {
        counts.set(c.completedBy, (counts.get(c.completedBy) ?? 0) + 1);
      }
      // Tylko dzieci — parent's "completion" to też family help dla rodziny,
      // ale w breakdown'ie rodzic byłby mylący ("Mama 2"). Pokazujemy
      // wkład dzieci.
      return children
        .map((m) => ({
          uid: m.userId,
          name: m.displayName,
          count: counts.get(m.userId) ?? 0,
        }))
        .filter((c) => c.count > 0)
        .sort((a, b) => b.count - a.count);
    };

    const familyHelpPerChild = buildPerChild(familyApproved);
    const offlinePerChild = buildPerChild(offlineApproved);
    const approvedPerChild = buildPerChild(approved);

    let topCompletion: Completion | null = null;
    const extras = approved.filter((c) => c.kind === "extra");
    if (extras.length > 0) {
      topCompletion = extras.reduce((max, c) =>
        c.rewardMinor > max.rewardMinor ? c : max,
      );
    } else if (familyApproved.length > 0) {
      topCompletion = familyApproved.reduce((latest, c) =>
        c.completedAt.toMillis() > latest.completedAt.toMillis() ? c : latest,
      );
    }

    let topMoment: RecapTopMoment | null = null;
    if (topCompletion) {
      const member = memberByUid.get(topCompletion.completedBy);
      topMoment = {
        childName: member?.displayName ?? "Ktoś z rodziny",
        choreTitle: topCompletion.choreTitle,
        dayName: format(topCompletion.completedAt.toDate(), "EEEE", { locale: pl }),
        rewardMinor: topCompletion.rewardMinor,
        kind: topCompletion.kind,
      };
    }

    // Cele oszczędzania viewer'a (individual_money). Pokazujemy do 3 najbliższych
    // ukończenia, niezaliczone first.
    const personalGoals: RecapPersonalGoal[] = [];
    if (viewerUid) {
      const me = memberByUid.get(viewerUid);
      const myBalance = me?.balanceMinor ?? 0;
      const myGoals = goals.filter(
        (g): g is IndividualMoneyGoal =>
          isIndividualMoneyGoal(g) && g.userId === viewerUid && !g.completedAt,
      );
      for (const g of myGoals) {
        const pct = Math.min(100, Math.round((myBalance / g.targetMinor) * 100));
        personalGoals.push({
          goalId: g.id,
          title: g.title,
          emoji: g.emoji,
          balanceMinor: myBalance,
          targetMinor: g.targetMinor,
          pct,
        });
      }
      personalGoals.sort((a, b) => b.pct - a.pct);
      personalGoals.splice(3);
    }

    // Aktywny cel rodzinny + progres (liczone od goal.createdAt z approvedAll).
    const activeFamilyGoal = householdGoals.find(
      (g) => !g.completedAt && isFamilyGoal(g),
    ) as FamilyCountGoal | FamilyOfflineCountGoal | undefined;
    let familyGoal: RecapFamilyGoalProgress | null = null;
    if (activeFamilyGoal) {
      const sinceMs = activeFamilyGoal.createdAt.toMillis();
      const matches = (c: Completion) =>
        activeFamilyGoal.kind === "family_count"
          ? c.kind === "family"
          : c.offline === true;
      let progress = 0;
      for (const c of approvedAll) {
        const approvedMs = c.approvedAt?.toMillis() ?? c.completedAt.toMillis();
        if (approvedMs < sinceMs) continue;
        if (!matches(c)) continue;
        progress += 1;
      }
      const pct = Math.min(
        100,
        Math.round((progress / activeFamilyGoal.targetCount) * 100),
      );
      familyGoal = { goal: activeFamilyGoal, progress, pct };
    }

    const approvedCount = approved.length;
    const offlineCount = offlineApproved.length;
    const totalCount = cur.completions.length;
    const hasAnyData =
      approvedCount > 0 ||
      familyApproved.length > 0 ||
      personalEarnedMinor > 0;

    return {
      loading: cur.loading || prev.loading || membersLoading || goalsLoading,
      weekStart: thisWeek.start,
      weekEnd: thisWeek.end,
      title: recapTitleFor(new Date()),
      personalEarnedMinor,
      prevPersonalEarnedMinor,
      diffPersonalEarnedMinor: personalEarnedMinor - prevPersonalEarnedMinor,
      familyHelpCount: familyApproved.length,
      familyHelpPerChild,
      offlineCount,
      offlinePerChild,
      approvedCount,
      approvedPerChild,
      totalCount,
      topMoment,
      personalGoals,
      familyGoal,
      hasAnyData,
    };
  }, [
    cur.completions,
    cur.loading,
    prev.completions,
    prev.loading,
    children,
    allMembers,
    membersLoading,
    goals,
    goalsLoading,
    householdGoals,
    approvedAll,
    viewerUid,
    thisWeek.start,
    thisWeek.end,
  ]);
}
